import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  useCmsConfig,
  ICoverageToolCongifg,
  getCompleteAddressValidation,
  CONSUMER_CMS_DEFAULT,
  organizeEditAddressFixed,
  getAddressFromLatLng,
} from '@vfit/consumer/data-access';
import { API, trackLink, trackView } from '@vfit/shared/data-access';
import { getPlaceId, getStreetNumberFromAutocomplete, useDeviceType } from '@vfit/shared/hooks';
import { LoggerInstance } from '@vfit/shared/data-access';
import { Button, Divider, CustomText } from '@vfit/shared/atoms';
import { Form } from '@vfit/shared/components';
import { fonts } from '@vfit/shared/themes';
import { IEditAddressCmsConfig } from '@vfit/shared/models';
import {
  ICoverageToolProps,
  ICoverageToolType,
  IGeoAddressElab,
  IInputs,
} from '../../../coverageToolModal.models';
import { useCoverageToolModalProvider } from '../../../coverageToolModal.context';
import {
  checkAddressFromValue,
  checkCoverageToolType,
  composeAddress,
  retrieveAddress,
} from '../../../coverageToolModal.utils';
import {
  Title,
  CoverageToolContainer,
  CoverageToolContent,
  CoverageToolContentButtonAuto,
} from '../../../coverageToolModal.style';
import CoverageToolAutoTagging from './coverageToolAuto.tagging';
import { ButtonCBU } from '@vfit/consumer/components';

const CoverageToolAuto = (props: ICoverageToolProps) => {
  const { isDesktop } = useDeviceType();
  const { product, setIsForcedManual, setAddress, setIsManual } = useCoverageToolModalProvider();
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [region, setRegion] = useState<string>('');
  const [autocompleteAddress, setAutocompleteAddress] = useState<string>('');
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoadingGeolocation, setIsLoadingGeolocation] = useState<boolean>(false);
  const coverageToolConfig = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_COVERAGE_TOOL_CONFIG
  ) as ICoverageToolCongifg;
  const editAddressStepper = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_EDIT_ADDRESS
  ) as IEditAddressCmsConfig;
  const {
    labels: { addressInputPlaceHolderAutocomplete },
  } = organizeEditAddressFixed(editAddressStepper?.editaddressconfig);
  const schema = getCompleteAddressValidation(coverageToolConfig);
  const errorMessageGeolocalizzation =
    coverageToolConfig?.coveragetool?.geolocalization?.alertError || '';
  const defaultValues = {
    ...props.state,
  };
  const initialState = {
    placeId: false,
    streetNumber: !(props.state?.street !== '' && props.state?.streetNumber !== ''),
  };
  const [hiddenFields, setHiddenFields] = useState(initialState);
  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IInputs>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'all',
  });

  const GetAddressObjectFromPlaceId = async (placeId: string) => {
    // eslint-disable-next-line @typescript-eslint/return-await
    return await getPlaceId(placeId);
  };

  const generateGoogleAddress = async (placeId: string | null, text?: string) => {
    //  Only if there is placeId
    if (placeId) {
      const addressObj = await GetAddressObjectFromPlaceId(placeId as string);
      //  Only if getAddressObject retireve an address object
      if (addressObj) {
        //  Shows street number text input at the first step if no retrievable from Google API placeId
        if (!addressObj?.streetNumber)
          setHiddenFields((prevState) => ({
            ...prevState,
            streetNumber: false,
          }));
        else {
          setValue('streetNumber', addressObj.streetNumber);
          await trigger(['streetNumber']);
        }
        setValue('placeId', placeId);
        if (addressObj.city) setValue('city', addressObj.city);
        if (addressObj.postalCode) setValue('postalCode', addressObj.postalCode);
        if (addressObj.street) setValue('street', addressObj.street);
        if (addressObj.stateOrProvince) setValue('stateOrProvince', addressObj.stateOrProvince);
        setLatitude(addressObj.latitude);
        setLongitude(addressObj.longitude);
        setRegion(addressObj.region || '');
        await trigger(['city', 'postalCode', 'street', 'stateOrProvince']);
      }
      const inputs = getValues();

      const googleAddress = {
        ...inputs,
        latitude: parseFloat(addressObj?.latitude || latitude),
        longitude: parseFloat(addressObj?.longitude || longitude),
      };
      setAddress({
        ...googleAddress,
        streetNumber:
          getStreetNumberFromAutocomplete(autocompleteAddress) || googleAddress?.streetNumber || '',
      });
    } else if (!placeId && !!text) {
      //  If placeId === null, it uses the input string 'text' to get the address object
      //  instead of the placeId. It uses useAddressGeocoder()
      const addressGeocoder = await checkAddressFromValue(text);

      if (addressGeocoder) {
        //  Check if with address from addrString + streetNumber can get all the fields.
        //  if not, it sets isManual to true and show all the form fields
        setLatitude(addressGeocoder.latitude);
        setLongitude(addressGeocoder.longitude);

        //  if all fields retrieved, it sets all of them
        if (addressGeocoder.placeId) setValue('placeId', addressGeocoder.placeId);
        if (addressGeocoder.city) setValue('city', addressGeocoder.city);
        if (addressGeocoder.postalCode) setValue('postalCode', addressGeocoder.postalCode);
        if (addressGeocoder.street) setValue('street', addressGeocoder.street);
        if (addressGeocoder.stateOrProvince)
          setValue('stateOrProvince', addressGeocoder.stateOrProvince);

        await trigger();

        const inputs = getValues();

        const googleAddress = {
          ...inputs,
          latitude: addressGeocoder.latitude ? parseFloat(addressGeocoder.latitude) : 0,
          longitude: addressGeocoder.longitude ? parseFloat(addressGeocoder.longitude) : 0,
        };
        setAddress({ ...googleAddress });
      }
    }
  };

  const placeId = watch('placeId');
  const streetNumber = watch('streetNumber');

  const onIconClick = () => {
    reset();
    setHiddenFields({
      placeId: false,
      streetNumber: true,
    });
  };

  const handleFirstClick = async () => {
    if (placeId && streetNumber && hiddenFields?.streetNumber === false) {
      const inputs = getValues();
      const formattedAddress = retrieveAddress({
        city: inputs.city,
        postalCode: inputs.postalCode,
        stateOrProvince: inputs.stateOrProvince,
        street: inputs.street,
        streetNumber,
      });
      await generateGoogleAddress(null, formattedAddress);
    }
    const updatedInputs = getValues();

    const googleAddress = {
      ...updatedInputs,
      ...(streetNumber && { streetNumber }),
      latitude: latitude ? parseFloat(latitude) : 0,
      longitude: longitude ? parseFloat(longitude) : 0,
      region,
    };

    setAddress({
      ...googleAddress,
      streetNumber:
        getStreetNumberFromAutocomplete(autocompleteAddress) || googleAddress?.streetNumber || '',
      typeCoverage: checkCoverageToolType(googleAddress),
    });
    setIsManual(checkCoverageToolType(googleAddress) === ICoverageToolType.MANUAL);
    props?.onSubmit?.();
  };

  const findPosition = async (p: GeolocationPosition) => {
    trackLink('click geolocalization');
    return new Promise((resolve) => {
      getAddressFromLatLng(p?.coords?.latitude, p?.coords?.longitude)
        .then((value: any) => {
          let address: IGeoAddressElab = {
            street: '',
            city: '',
            province: '',
            postalCode: '',
          };
          if (value !== undefined) {
            setHiddenFields((prevState: any) => ({
              ...prevState,
              streetNumber: false,
            }));
            address = composeAddress(value);
          }
          resolve(address);
          if (address.latitude) setLatitude(address.latitude);
          if (address.longitude) setLongitude(address.longitude);
          if (address.streetNumber) setValue('streetNumber', address.streetNumber);
          if (address.postalCode) setValue('postalCode', address.postalCode);
          if (address.province) setValue('stateOrProvince', address.province.toUpperCase());
          if (address.city) setValue('city', address.city.toUpperCase());
          if (address.street) setValue('street', address.street);
          if (address.placeId) setValue('placeId', address.placeId);
          if (address.region) setRegion(address.region || '');
        })
        .catch((err) => {
          LoggerInstance.debug(err);
        });
    });
  };

  const handleLoadingGeolocation = (isLoadingGeo: boolean) => {
    setIsLoadingGeolocation(isLoadingGeo);
  };

  useEffect(() => {
    if (placeId && !streetNumber && hiddenFields?.streetNumber === true) {
      generateGoogleAddress(placeId);
    }
    //  to handle abling/disabling button
    if (streetNumber && !errors.streetNumber && placeId) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [placeId, streetNumber, hiddenFields.streetNumber, setValue]);

  useEffect(() => {
    if (!placeId) {
      setValue('streetNumber', '');
    }
  }, [placeId]);

  useEffect(() => {
    setIsFormDisabled(false);
    setIsForcedManual(false);
  }, []);

  return (
    <>
      {product && <CoverageToolAutoTagging />}
      <CoverageToolContainer>
        <CoverageToolContent>
          <Title>{coverageToolConfig?.coveragetool?.addressInput?.title || ' '}</Title>
          <CustomText
            text={coverageToolConfig?.coveragetool?.addressInput?.description || ' '}
            textAlign="center"
            size={20}
            lineHeight={26}
            fontFamily={fonts.regular}
            modal
          />
          <Form
            autoComplete="off"
            onKeyPress={(e) => {
              if (e?.key === 'Enter') e?.preventDefault();
            }}
          >
            <Controller
              control={control}
              name="placeId"
              render={({ field: { onChange, onBlur, ref } }) => (
                <Form.AddressInput
                  data-cs-mask=""
                  label={coverageToolConfig?.coveragetool?.selectInput?.address || ''}
                  onChange={(placeId, autocompleteAddress) => {
                    setHiddenFields((prevState) => ({
                      ...prevState,
                      streetNumber: true,
                    }));
                    setAutocompleteAddress(autocompleteAddress || '');
                    onChange(placeId);
                  }}
                  hidden={hiddenFields.placeId}
                  autoFocus={isDesktop}
                  autoComplete="off"
                  onIconClick={onIconClick}
                  onBlur={onBlur}
                  ref={ref}
                  disabled={isFormDisabled || isLoadingGeolocation}
                  findPosition={findPosition}
                  onChangeGeolocation={handleLoadingGeolocation}
                  errorMessage={errorMessageGeolocalizzation}
                  placeholderAutocomplete={{
                    enabled: true,
                    label: addressInputPlaceHolderAutocomplete,
                    action: () => {
                      handleFirstClick();
                      setIsForcedManual(true);
                    },
                  }}
                />
              )}
            />
            <Divider marginBottom={1} hidden={hiddenFields.streetNumber} />
            <Form.TextInput
              data-cs-mask=""
              placeholder=" "
              label={coverageToolConfig?.coveragetool?.selectInput?.civicNumber || ''}
              hidden={hiddenFields.streetNumber}
              error={!hiddenFields.streetNumber ? errors.streetNumber?.message : ' '}
              autoFocus
              autoComplete="off"
              disabled={isFormDisabled}
              onError={() =>
                trackView({
                  event_name: 'form_error',
                  event_label_track: 'error',
                  event_category: 'please complete street number',
                })
              }
              {...register('streetNumber')}
            />
          </Form>
        </CoverageToolContent>
        <CoverageToolContentButtonAuto>
          <ButtonCBU
            disabled={isDisabled}
            onClick={handleFirstClick}
            variant={'primary'}
            name="action_goToCheckCoverage"
          >
            {coverageToolConfig?.coveragetool?.addressInput?.button || ''}
          </ButtonCBU>
        </CoverageToolContentButtonAuto>
      </CoverageToolContainer>
    </>
  );
};

export default CoverageToolAuto;
